import { ref, unref, onBeforeUnmount } from 'vue'
import {
  clearRegFlowDeepLinkPending,
  normalizeAvailChartsLockRoute,
  shouldActivateLocationAvChartDeepLinkFilters,
  tightenRegFlowChartHashInLocationBar,
  toAvailChartsLockPulseHash,
} from '~/utils/location-av-chart-deeplink'
import { usePulsePrefetch } from './usePulsePrefetch'
import { useErrorLogger } from './useErrorLogger'

export const usePulseChartFallback = () => {
  const showPulseChartError = ref(false)
  const { getPulseRouteStatus } = usePulsePrefetch()
  const { logError } = useErrorLogger()
  // Fallback contract: if the chart never shows real Pulse content within this window, show the message.
  const DEFAULT_TIMEOUT_MS = 20000
  // Poll shared route status at a light interval; no network calls are made here.
  const POLL_INTERVAL_MS = 250
  let intervalId = null
  let timeoutId = null
  let filterActivated = false

  // Resolve the Pulse route we should monitor for this chart instance.
  // We read from `data-page` to match how Pulse containers declare their route.
  const resolveRoutePath = (containerRef) => {
    const current = unref(containerRef)
    const root = current?.closest?.('[data-page]') || current
    const dataPage = root?.getAttribute?.('data-page') || ''
    return dataPage || ''
  }

  // AVC chart mount tags (see Location/AVC.vue). Narrow selector avoids querySelectorAll('*') on every poll.
  const PULSE_CHART_MOUNT_SELECTOR = 'idp-avc-availability-chart, idp-avc-availability-chart-virtual'

  const hasPulseRenderedContent = (containerRef) => {
    const current = unref(containerRef)
    if (!current) return false
    const pulseRoot = current.classList?.contains('pulseapp') ? current : current.querySelector('.pulseapp')
    if (!pulseRoot) return false

    return pulseRoot.querySelector(PULSE_CHART_MOUNT_SELECTOR) !== null
  }

  const stopPulseChartMonitoring = () => {
    if (intervalId !== null) {
      window.clearInterval(intervalId)
      intervalId = null
    }

    if (timeoutId !== null) {
      window.clearTimeout(timeoutId)
      timeoutId = null
    }
  }

  /**
   * Start monitoring one chart container.
   * @param {Ref<Element|null>|Element|null} containerRef
   * @param {{ routePath?: string, timeoutMs?: number }|number} options
   */
  const startPulseChartMonitoring = (containerRef, options = {}) => {
    if (!import.meta.client) return
    stopPulseChartMonitoring()
    showPulseChartError.value = false

    // Prefer explicit routePath when provided; otherwise infer from DOM container.
    const routePath = options?.routePath || resolveRoutePath(containerRef)
    if (!routePath) {
      logError(new Error('Pulse chart fallback: missing route path; monitoring not started'), {
        context: 'usePulseChartFallback',
        hasExplicitRoutePath: Boolean(options?.routePath),
      })
      return
    }
    const timeoutMs = typeof options === 'number' ? options : (options?.timeoutMs ?? DEFAULT_TIMEOUT_MS)

    const applyLatestRouteStatus = () => {
      // During a deep-link, do NOT tighten the hash via replaceState.
      // tightenRegFlowChartHashInLocationBar converts Pulse's gnrid[]=... to scalar gnrid=...
      // (no hashchange). This cosmetic change fools the detection below into thinking gnrid
      // was already applied by Pulse (via hashchange), so it skips the location.href navigation
      // — but AngularJS's $location never saw gnrid and the filter is never applied.
      // Only tighten for non-deep-link states (normalizing bracket params after filter clears).
      if (!shouldActivateLocationAvChartDeepLinkFilters()) {
        tightenRegFlowChartHashInLocationBar({ dataPage: routePath, refreshPulse: false })
      }

      if (!hasPulseRenderedContent(containerRef)) {
        return false
      }

      showPulseChartError.value = false

      if (shouldActivateLocationAvChartDeepLinkFilters() && !filterActivated) {
        filterActivated = true
        stopPulseChartMonitoring()

        const normalized = normalizeAvailChartsLockRoute(
          routePath.startsWith('/') ? routePath : `/${routePath}`,
        )

        // Navigate via real hashchange so AngularJS's $location sees gnrid/prodid.
        // Leland confirmed: scalar gnrid=295001 in the URI/hash is the supported approach;
        // his Pulse fix keeps gnrid in the hash and fires a post-render event to populate
        // the genre dropdown. Do NOT call refreshRoute() after this navigation.
        //
        // Use Pulse's native array form (gnrid[]=295001, lid[]=63) which matches the format
        // Pulse uses internally when it serializes its route state.
        //
        // We always navigate here (do not skip based on hash check): hasPulseRenderedContent
        // fires when idp-avc-availability-chart appears in the DOM, which can be before
        // Leland's post-render event populates the genre filter. Skipping navigation would
        // leave the filter unapplied even though the element exists.
        const pulseHash = toAvailChartsLockPulseHash(normalized)
        window.location.href = pulseHash.startsWith('#') ? pulseHash : `#${pulseHash}`
        // Keep pending true for 2s to block MutationObserver-triggered refreshRoute
        // during the window while AngularJS applies the gnrid filter.
        window.setTimeout(clearRegFlowDeepLinkPending, 2000)
        return true
      }

      clearRegFlowDeepLinkPending()
      stopPulseChartMonitoring()
      return true
    }

    // Immediately bail out if we already have a definitive state.
    if (applyLatestRouteStatus()) return

    intervalId = window.setInterval(() => {
      applyLatestRouteStatus()
    }, POLL_INTERVAL_MS)

    timeoutId = window.setTimeout(() => {
      const chartRendered = hasPulseRenderedContent(containerRef)
      // After the window, missing chart content is a failure even if prefetch reported success.
      const failed = !chartRendered
      showPulseChartError.value = failed
      if (failed) {
        const { status, updatedAt } = getPulseRouteStatus(routePath)
        logError(new Error('Pulse chart fallback: chart did not render before timeout'), {
          context: 'usePulseChartFallback',
          routePath,
          timeoutMs,
          prefetchStatus: status,
          prefetchStatusUpdatedAt: updatedAt,
          chartRendered,
        })
      }
      stopPulseChartMonitoring()
    }, timeoutMs)
  }

  onBeforeUnmount(() => {
    stopPulseChartMonitoring()
  })

  return {
    showPulseChartError,
    startPulseChartMonitoring,
    stopPulseChartMonitoring
  }
}
